import {
  Cancel01Icon,
  InformationCircleIcon
} from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import {
  Button,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Input,
  Show
} from "shared/components";
import { Modal } from "shared/components/Modal";
import { useFormTyped } from "shared/hooks";
import { getFullName } from "shared/utils/get-full-name";
import { Applicant } from "types/application";
import { formatToTwoDecimals } from "utils/format-to-two-decimals";
import { getApplicantRole } from "utils/getApplicantRole";

export const EditIncomeModal = ({
  opened,
  onClose,
  applicant,
  verificationId,
  applicationId,
  grossMonthlyIncomeDollars,
  netMonthlyIncomeDollars,
  hasNetMonthlyIncome,
  isTotal,
  incomeId,
  onSuccess
}: {
  opened: boolean;
  onClose: () => void;
  applicant: Applicant;
  verificationId: number;
  applicationId: number;
  grossMonthlyIncomeDollars?: number;
  netMonthlyIncomeDollars?: number;
  hasNetMonthlyIncome?: boolean;
  incomeId?: number;
  isTotal?: boolean;
  onSuccess?: () => void;
}) => {
  const queryClient = useQueryClient();
  const form = useFormTyped({
    gross: grossMonthlyIncomeDollars || 0,
    net: netMonthlyIncomeDollars || 0
  });

  const mutation = useMutation({
    mutationKey: [
      "EDIT_INCOME",
      applicationId,
      verificationId,
      applicant.id,
      isTotal
    ],
    mutationFn: (data: { gross: string; net: string }) => {
      const endpoint = isTotal
        ? `portal/v1/applications/${applicationId}/verifications/${verificationId}/create_override?applicant_id=${applicant.id}`
        : `portal/v1/applications/${applicationId}/verifications/${verificationId}/create_override/income/${incomeId}?applicant_id=${applicant.id}`;
      return instance
        .post(endpoint, {
          gross_monthly_income: parseFloat(data.gross),
          net_monthly_income: hasNetMonthlyIncome
            ? parseFloat(data.net)
            : undefined
        })
        .then(({ data }) => {
          return data;
        });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["APPLICATION", applicationId]
      });
    }
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      panelClassName="max-w-[477px] w-full"
    >
      <form
        onSubmit={form.onSubmit({
          onSuccess: (values) => {
            mutation.mutate(
              {
                gross: formatToTwoDecimals(values.gross),
                net: formatToTwoDecimals(values.net)
              },
              {
                onSuccess: () => {
                  onSuccess?.();
                  onClose();
                }
              }
            );
          }
        })}
      >
        <header className="px-6 py-5 text-lg font-bold flex justify-between items-center border-b border-alpha-100">
          Edit total verified income
          <IconButton
            HugeIcon={Cancel01Icon}
            size={IconButtonSize.SM}
            variant={IconButtonVariant.TEXT_BASE}
            onClick={onClose}
          />
        </header>
        <div className="px-6 py-4 bg-warning-50 border-b border-alpha-100 flex gap-4 items-center">
          <HugeiconsIcon
            icon={InformationCircleIcon}
            size={24}
            className="text-warning-700 shrink-0"
          />
          <p className="text-sm text-warning-700 font-medium">
            Please note that editing this income will lead to an application
            score recalculation. Decision recommendations may change.
          </p>
        </div>
        <div className="px-6 py-5 space-y-1 border-b border-alpha-100">
          <h2 className="text-base font-medium text-content-1000">
            {getFullName({
              firstName: applicant.first_name,
              lastName: applicant.last_name,
              middleName: applicant.middle_name,
              short: true
            })}
          </h2>

          <p className="text-sm text-content-400 font-medium">
            {getApplicantRole(applicant.role)}
          </p>
        </div>

        <fieldset className="bg-background-900 p-6">
          <Input
            id="gross"
            label="Verified gross monthly income"
            type="money"
            hideOptional
            {...form.register("gross")}
          />
          <Show if={hasNetMonthlyIncome}>
            <div className="mt-2">
              <Input
                id="net"
                label="Verified net monthly income"
                type="money"
                hideOptional
                {...form.register("net")}
              />
            </div>
          </Show>
        </fieldset>

        <div className="px-6 py-4 flex gap-2 justify-end border-t border-alpha-100">
          <Button className="w-[125px]" onClick={onClose}>
            Cancel
          </Button>

          <Button
            className="w-[125px]"
            variant={ButtonVariant.PRIMARY}
            type="submit"
            loading={mutation.isPending}
          >
            Save changes
          </Button>
        </div>
      </form>
    </Modal>
  );
};
